import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest, TOKEN_KEY } from './api/client';
import AppLayout from './components/AppLayout';
import AnalyticsPage from './pages/AnalyticsPage';
import AppUsersPage from './pages/AppUsersPage';
import AuthPage from './pages/AuthPage';
import OverviewPage from './pages/OverviewPage';
import LogsPage from './pages/LogsPage';
import PlacesPage from './pages/PlacesPage';
import ComplexesPage from './pages/ComplexesPage';
import PlaceCategoriesPage from './pages/PlaceCategoriesPage';
import PlaceIssueReportsPage from './pages/PlaceIssueReportsPage';
import PlaceEditSubmissionsPage from './pages/PlaceEditSubmissionsPage';
import ReportsPage from './pages/ReportsPage';
import GpsRawReportsPage from './pages/GpsRawReportsPage';
import GpsLoadSettingsPage from './pages/GpsLoadSettingsPage';
import LoadAlgorithmSettingsPage from './pages/LoadAlgorithmSettingsPage';
import AiAlgorithmSettingsPage from './pages/AiAlgorithmSettingsPage';
import AutoVisitSettingsPage from './pages/AutoVisitSettingsPage';
import AutoVisitReportsPage from './pages/AutoVisitReportsPage';
import OsmImportPage from './pages/OsmImportPage';
import './App.css';
import './categories.css';
import { Button, Typography, Stack } from '@mui/material';
const emptyFilters = {
  search: '',
  status: 'active',
  isVerified: '',
  footprintStatus: '',
  placeKind: '',
  source: '',
  eventType: '',
  placeId: '',
  from: '',
  to: '',
  issueSearch: '',
  issueStatus: '',
  issueSubject: '',
  userSearch: '',
  editSearch: '',
  editStatus: '',
  logSearch: '',
  logType: '',
  logPlatform: '',
  logEnvironment: '',
  gpsFrom: '',
  gpsTo: '',
  gpsStatus: '',
  gpsSource: '',
  gpsPlaceId: '',
};
const toDateInputValue = (date) => date.toISOString().slice(0, 10);
const getDefaultAnalyticsDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 1);
  return {
    from: toDateInputValue(from),
    to: toDateInputValue(to),
  };
};
const App = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [user, setUser] = useState(null);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  const [bootstrapUser, setBootstrapUser] = useState(null);
  const [canDevReset, setCanDevReset] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [places, setPlaces] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
  });
  const [placeCategories, setPlaceCategories] = useState([]);
  const [reports, setReports] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
  });
  const [gpsRawReports, setGpsRawReports] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
    summary: {},
  });
  const [analytics, setAnalytics] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
    reports: {},
  });
  const [overview, setOverview] = useState({
    metrics: {},
    daily: [],
  });
  const [placeIssueReports, setPlaceIssueReports] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
  });
  const [placeEditSubmissions, setPlaceEditSubmissions] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
  });
  const [blockedPlaceEditPhones, setBlockedPlaceEditPhones] = useState([]);
  const [appUsers, setAppUsers] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
  });
  const [logs, setLogs] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
  });
  const [gpsLoadSettings, setGpsLoadSettings] = useState(null);
  const [loadAlgorithmSettings, setLoadAlgorithmSettings] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [authForm, setAuthForm] = useState({
    name: '',
    email: '',
    password: '',
  });
  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );
  const messageType = useMemo(() => {
    if (!message) return '';
    return message.includes('נשמר') ||
      message.includes('עודכן') ||
      message.includes('נמחק') ||
      message.includes('הושלמה')
      ? 'success'
      : 'error';
  }, [message]);
  const request = useCallback(
    (path, options = {}) =>
      apiRequest(path, {
        ...options,
        headers: {
          ...(token ? authHeaders : {}),
          ...(options.headers || {}),
        },
      }),
    [authHeaders, token],
  );
  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      setOverview(await request('/bo/overview'));
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [request]);
  const loadPlaces = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
        });
        if (filters.search) params.set('search', filters.search);
        if (filters.status) params.set('status', filters.status);
        if (filters.isVerified) params.set('isVerified', filters.isVerified);
        if (filters.footprintStatus) params.set('footprintStatus', filters.footprintStatus);
        if (activeTab === 'complexes') {
          params.set('placeKind', 'container');
        } else {
          params.set('excludePlaceKind', 'container');
          if (filters.placeKind) params.set('placeKind', filters.placeKind);
        }
        setPlaces(await request(`/bo/places?${params}`));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [
      filters.footprintStatus,
      filters.isVerified,
      filters.placeKind,
      filters.search,
      filters.status,
      activeTab,
      request,
    ],
  );
  const loadPlaceCategories = useCallback(async () => {
    setLoading(true);
    try {
      setPlaceCategories(await request('/bo/place-categories'));
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [request]);
  const loadReports = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
        });
        ['source', 'placeId', 'from', 'to'].forEach(
          (key) => filters[key] && params.set(key, filters[key]),
        );
        setReports(await request(`/bo/reports?${params}`));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters, request],
  );
  const loadGpsRawReports = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
        });
        if (filters.gpsFrom) params.set('from', filters.gpsFrom);
        if (filters.gpsTo) params.set('to', filters.gpsTo);
        if (filters.gpsStatus) params.set('status', filters.gpsStatus);
        if (filters.gpsSource) params.set('source', filters.gpsSource);
        if (filters.gpsPlaceId) params.set('placeId', filters.gpsPlaceId);
        setGpsRawReports(await request(`/bo/gps-raw-reports?${params}`));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [
      filters.gpsFrom,
      filters.gpsPlaceId,
      filters.gpsSource,
      filters.gpsStatus,
      filters.gpsTo,
      request,
    ],
  );
  const loadAnalytics = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
        });
        const defaultDateRange = getDefaultAnalyticsDateRange();
        params.set('from', filters.from || defaultDateRange.from);
        params.set('to', filters.to || defaultDateRange.to);
        ['source', 'eventType', 'placeId'].forEach(
          (key) => filters[key] && params.set(key, filters[key]),
        );
        setAnalytics(await request(`/bo/analytics?${params}`));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters, request],
  );
  const loadPlaceIssueReports = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
        });
        if (filters.issueSearch) params.set('search', filters.issueSearch);
        if (filters.issueStatus) params.set('status', filters.issueStatus);
        if (filters.issueSubject) params.set('subject', filters.issueSubject);
        setPlaceIssueReports(await request(`/bo/place-issue-reports?${params}`));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters.issueSearch, filters.issueStatus, filters.issueSubject, request],
  );
  const loadPlaceEditSubmissions = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
        });
        if (filters.editSearch) params.set('search', filters.editSearch);
        if (filters.editStatus) params.set('status', filters.editStatus);
        setPlaceEditSubmissions(await request(`/bo/place-edit-submissions?${params}`));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters.editSearch, filters.editStatus, request],
  );
  const loadBlockedPlaceEditPhones = useCallback(async () => {
    setBlockedPlaceEditPhones(await request('/bo/place-edit-blocked-phones'));
  }, [request]);
  const loadAppUsers = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
        });
        if (filters.userSearch) params.set('search', filters.userSearch);
        setAppUsers(await request(`/bo/app-users?${params}`));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters.userSearch, request],
  );
  const loadLogs = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const result = await request('/logs');
        const items = Array.isArray(result) ? result : [];
        const search = filters.logSearch.trim().toLowerCase();
        const filteredItems = items
          .filter(
            (log) =>
              !search ||
              String(log.description || '')
                .toLowerCase()
                .includes(search),
          )
          .filter((log) => !filters.logType || String(log.type) === filters.logType)
          .filter((log) => !filters.logPlatform || String(log.platform) === filters.logPlatform)
          .filter((log) => {
            if (!filters.logEnvironment) return true;
            return filters.logEnvironment === 'production'
              ? log.isProduction !== false
              : log.isProduction === false;
          })
          .sort((a, b) => new Date(b.created || 0) - new Date(a.created || 0));
        const limit = 50;
        const safePage = Math.max(1, Math.min(page, Math.ceil(filteredItems.length / limit) || 1));
        const start = (safePage - 1) * limit;
        setLogs({
          items: filteredItems.slice(start, start + limit),
          total: filteredItems.length,
          page: safePage,
          limit,
        });
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters.logEnvironment, filters.logPlatform, filters.logSearch, filters.logType, request],
  );
  const loadGpsLoadSettings = useCallback(async () => {
    setLoading(true);
    try {
      setGpsLoadSettings(await request('/bo/gps-load-settings'));
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [request]);
  const loadLoadAlgorithmSettings = useCallback(async () => {
    setLoading(true);
    try {
      setLoadAlgorithmSettings(await request('/bo/load-algorithm-settings'));
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [request]);
  useEffect(() => {
    document.documentElement.setAttribute('dir', 'rtl');
    document.documentElement.setAttribute('lang', 'he');
    document.body.setAttribute('dir', 'rtl');
  }, []);
  useEffect(() => {
    if (!message) return undefined;
    const timeoutId = setTimeout(() => {
      setMessage('');
    }, 10000);
    return () => clearTimeout(timeoutId);
  }, [message]);
  useEffect(() => {
    apiRequest('/bo/auth/bootstrap-status')
      .then((data) => {
        setNeedsBootstrap(Boolean(data.needsBootstrap));
        setBootstrapUser(data.user || null);
        setCanDevReset(Boolean(data.canDevReset));
        if (data.user?.email)
          setAuthForm((current) => ({
            ...current,
            email: current.email || data.user.email,
          }));
      })
      .catch(() => setMessage('לא ניתן לבדוק סטטוס התחברות'));
  }, []);
  useEffect(() => {
    if (!token) return;
    request('/bo/me')
      .then(setUser)
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      });
  }, [request, token]);
  useEffect(() => {
    if (!token || !user) return;
    if (activeTab === 'overview') loadOverview();
    if (activeTab === 'places' || activeTab === 'complexes') {
      loadPlaces();
      loadPlaceCategories();
      loadGpsLoadSettings();
    }
    if (activeTab === 'categories') loadPlaceCategories();
    if (activeTab === 'reports') loadReports();
    if (activeTab === 'gpsRawReports') loadGpsRawReports();
    if (activeTab === 'analytics') loadAnalytics();
    if (activeTab === 'placeIssues') loadPlaceIssueReports();
    if (activeTab === 'placeEdits') {
      loadPlaceEditSubmissions();
      loadBlockedPlaceEditPhones();
    }
    if (activeTab === 'appUsers') loadAppUsers();
    if (activeTab === 'logs') loadLogs();
    if (activeTab === 'gpsSettings') loadGpsLoadSettings();
    if (activeTab === 'loadAlgorithm') loadLoadAlgorithmSettings();
  }, [
    activeTab,
    loadAppUsers,
    loadLogs,
    loadGpsLoadSettings,
    loadLoadAlgorithmSettings,
    loadGpsRawReports,
    loadAnalytics,
    loadOverview,
    loadPlaceCategories,
    loadPlaceIssueReports,
    loadPlaceEditSubmissions,
    loadBlockedPlaceEditPhones,
    loadPlaces,
    loadReports,
    token,
    user,
  ]);
  const submitAuth = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const path = needsBootstrap ? '/bo/auth/bootstrap' : '/bo/auth/login';
      const payload = needsBootstrap
        ? authForm
        : {
            email: authForm.email,
            password: authForm.password,
          };
      const data = await apiRequest(path, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setUser(data.user);
      setNeedsBootstrap(false);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setUser(null);
  };
  const savePlace = async (placeId, payload) => {
    setLoading(true);
    setMessage('');
    try {
      const savedPlace = await request(`/bo/places/${placeId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      const updatedPlace = savedPlace?.place || savedPlace;
      setPlaces((current) => ({
        ...current,
        items: current.items.map((place) =>
          place.placeId === placeId
            ? {
                ...place,
                ...payload,
                ...(updatedPlace && typeof updatedPlace === 'object' ? updatedPlace : {}),
              }
            : place,
        ),
      }));
      setMessage('המקום נשמר');
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const saveGpsLoadSettings = async (payload) => {
    setLoading(true);
    setMessage('');
    try {
      await request('/bo/gps-load-settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setMessage('הגדרות עומס GPS נשמרו');
      await loadGpsLoadSettings();
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const saveLoadAlgorithmSettings = async (payload) => {
    setLoading(true);
    setMessage('');
    try {
      await request('/bo/load-algorithm-settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setMessage('הגדרות אלגוריתם העומס נשמרו');
      await loadLoadAlgorithmSettings();
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const loadNearbyPlaceFootprints = useCallback(
    (placeId) => request(`/bo/places/${placeId}/nearby-footprints?radius=500`),
    [request],
  );
  const loadContainerManagement = useCallback(
    (placeId, search = '') =>
      request(
        `/bo/places/${placeId}/container${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      ),
    [request],
  );
  const updateContainerFloors = useCallback(
    (placeId, payload) =>
      request(`/bo/places/${placeId}/container/floors`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    [request],
  );
  const manageContainerFloorUnit = useCallback(
    (placeId, unitId, action, payload) => {
      const isBulk = action === 'bulk';
      const suffix = action === 'assign' || action === 'unassign' ? '/place' : '';
      const method =
        action === 'delete' || action === 'unassign'
          ? 'DELETE'
          : action === 'assign'
            ? 'POST'
            : 'PUT';
      const path = isBulk
        ? `/bo/places/${placeId}/container/units/assignments`
        : `/bo/places/${placeId}/container/units/${unitId}${suffix}`;
      return request(path, {
        method,
        ...(payload
          ? {
              body: JSON.stringify(payload),
            }
          : {}),
      });
    },
    [request],
  );
  const assignPlaceToContainer = useCallback(
    (placeId, payload) =>
      request(`/bo/places/${placeId}/container/places`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    [request],
  );
  const removePlaceFromContainer = useCallback(
    (placeId, childId) =>
      request(`/bo/places/${placeId}/container/places/${childId}`, {
        method: 'DELETE',
      }),
    [request],
  );
  const deletePlace = async (placeId, confirmationName) => {
    setLoading(true);
    setMessage('');
    try {
      await request(`/bo/places/${placeId}`, {
        method: 'DELETE',
        body: JSON.stringify({
          confirmationName,
        }),
      });
      setMessage('המקום נמחק');
      await loadPlaces(places.page || 1);
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const savePlaceIssueReport = async (reportId, payload) => {
    setLoading(true);
    setMessage('');
    try {
      await request(`/bo/place-issue-reports/${reportId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setMessage('הדיווח עודכן');
      await loadPlaceIssueReports(placeIssueReports.page || 1);
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const handlePlaceEdit = async (id, action, payload) => {
    setLoading(true);
    setMessage('');
    try {
      await request(`/bo/place-edit-submissions/${id}${action ? `/${action}` : ''}`, {
        method: action ? 'POST' : 'PUT',
        body: JSON.stringify(payload),
      });
      setMessage(
        action === 'approve'
          ? 'ההצעה אושרה והמקום עודכן'
          : action === 'reject'
            ? 'ההצעה נדחתה'
            : 'טיוטת ההצעה נשמרה',
      );
      await loadPlaceEditSubmissions(placeEditSubmissions.page || 1);
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const blockPlaceEditPhone = async (phone, reason) => {
    await request('/bo/place-edit-blocked-phones', {
      method: 'POST',
      body: JSON.stringify({
        phone,
        reason,
      }),
    });
    setMessage('מספר הטלפון נחסם');
    await Promise.all([
      loadPlaceEditSubmissions(placeEditSubmissions.page || 1),
      loadBlockedPlaceEditPhones(),
    ]);
  };
  const unblockPlaceEditPhone = async (phone) => {
    await request(`/bo/place-edit-blocked-phones/${encodeURIComponent(phone)}`, {
      method: 'DELETE',
    });
    setMessage('חסימת הטלפון בוטלה');
    await Promise.all([
      loadPlaceEditSubmissions(placeEditSubmissions.page || 1),
      loadBlockedPlaceEditPhones(),
    ]);
  };
  const deleteAppUser = async (user) => {
    const identifier = user.userId || user.anonymousId || user.deviceId || user.id;
    const approved = window.confirm(
      `למחוק את המשתמש ${identifier} וכל המידע שקשור אליו?\n\nזה ימחק דיווחים, נקודות, מועדפים, היסטוריית חיפוש, סשנים ואירועים. הפעולה לא הפיכה.`,
    );
    if (!approved) return;
    setLoading(true);
    setMessage('');
    try {
      const result = await request(`/bo/app-users/${user.id}`, {
        method: 'DELETE',
      });
      const deleted = result.deleted || {};
      setMessage(`המשתמש נמחק (${deleted.reports || 0} דיווחים)`);
      await loadAppUsers(appUsers.page || 1);
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const saveAppUserFullName = async (user, fullName) => {
    setLoading(true);
    setMessage('');
    try {
      await request(`/bo/app-users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          fullName,
        }),
      });
      setMessage('השם המלא נשמר');
      await loadAppUsers(appUsers.page || 1);
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const createPlaceCategory = async (payload) => {
    setLoading(true);
    setMessage('');
    try {
      await request('/bo/place-categories', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setMessage('הקטגוריה נוספה');
      await loadPlaceCategories();
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const savePlaceCategory = async (id, payload) => {
    setLoading(true);
    setMessage('');
    try {
      await request(`/bo/place-categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setMessage('הקטגוריה נשמרה');
      await loadPlaceCategories();
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const deletePlaceCategory = async (category) => {
    if (!window.confirm(`למחוק את הקטגוריה ${category.displayName}?`)) return;
    setLoading(true);
    setMessage('');
    try {
      await request(`/bo/place-categories/${category._id}`, {
        method: 'DELETE',
      });
      setMessage('הקטגוריה נמחקה');
      await loadPlaceCategories();
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const renderPage = () => {
    if (activeTab === 'overview') return <OverviewPage overview={overview} />;
    if (activeTab === 'osmImport') return <OsmImportPage request={request} />;
    if (activeTab === 'categories')
      return (
        <PlaceCategoriesPage
          categories={placeCategories}
          onCreate={createPlaceCategory}
          onSave={savePlaceCategory}
          onDelete={deletePlaceCategory}
          loading={loading}
        />
      );
    if (activeTab === 'reports')
      return (
        <ReportsPage
          reports={reports}
          filters={filters}
          setFilters={setFilters}
          loadReports={loadReports}
          loading={loading}
        />
      );
    if (activeTab === 'gpsRawReports')
      return (
        <GpsRawReportsPage
          reports={gpsRawReports}
          filters={filters}
          setFilters={setFilters}
          loadReports={loadGpsRawReports}
          loading={loading}
        />
      );
    if (activeTab === 'autoVisitReports') return <AutoVisitReportsPage request={request} />;
    if (activeTab === 'placeIssues')
      return (
        <PlaceIssueReportsPage
          reports={placeIssueReports}
          filters={filters}
          setFilters={setFilters}
          loadReports={loadPlaceIssueReports}
          onSaveReport={savePlaceIssueReport}
          loading={loading}
        />
      );
    if (activeTab === 'placeEdits')
      return (
        <PlaceEditSubmissionsPage
          submissions={placeEditSubmissions}
          filters={filters}
          setFilters={setFilters}
          loadSubmissions={loadPlaceEditSubmissions}
          onSave={(id, payload) => handlePlaceEdit(id, '', payload)}
          onApprove={(id, payload) => handlePlaceEdit(id, 'approve', payload)}
          onReject={(id, payload) => handlePlaceEdit(id, 'reject', payload)}
          onBlock={blockPlaceEditPhone}
          blockedPhones={blockedPlaceEditPhones}
          onUnblock={unblockPlaceEditPhone}
          loading={loading}
        />
      );
    if (activeTab === 'analytics')
      return (
        <AnalyticsPage
          analytics={analytics}
          filters={filters}
          setFilters={setFilters}
          loadAnalytics={loadAnalytics}
          loading={loading}
        />
      );
    if (activeTab === 'appUsers')
      return (
        <AppUsersPage
          users={appUsers}
          filters={filters}
          setFilters={setFilters}
          loadUsers={loadAppUsers}
          onDeleteUser={deleteAppUser}
          onSaveUserFullName={saveAppUserFullName}
          loading={loading}
        />
      );
    if (activeTab === 'autoVisitSettings') return <AutoVisitSettingsPage />;
    if (activeTab === 'loadAlgorithm')
      return (
        <LoadAlgorithmSettingsPage
          settings={loadAlgorithmSettings}
          onSave={saveLoadAlgorithmSettings}
          loading={loading}
        />
      );
    if (activeTab === 'aiAlgorithm')
      return <AiAlgorithmSettingsPage request={request} user={user} />;
    if (activeTab === 'gpsSettings')
      return (
        <GpsLoadSettingsPage
          settings={gpsLoadSettings}
          onSave={saveGpsLoadSettings}
          loading={loading}
        />
      );
    if (activeTab === 'logs')
      return (
        <LogsPage
          logs={logs}
          filters={filters}
          setFilters={setFilters}
          loadLogs={loadLogs}
          loading={loading}
        />
      );
    const PlaceListPage = activeTab === 'complexes' ? ComplexesPage : PlacesPage;
    return (
      <PlaceListPage
        places={places}
        filters={filters}
        setFilters={setFilters}
        loadPlaces={loadPlaces}
        onSavePlace={savePlace}
        onLoadNearbyPlaceFootprints={loadNearbyPlaceFootprints}
        onLoadContainerManagement={loadContainerManagement}
        onUpdateContainerFloors={updateContainerFloors}
        onManageContainerFloorUnit={manageContainerFloorUnit}
        onAssignPlaceToContainer={assignPlaceToContainer}
        onRemovePlaceFromContainer={removePlaceFromContainer}
        onDeletePlace={deletePlace}
        behaviorTypes={(gpsLoadSettings?.behaviorTypes || []).filter((item) => item.enabled)}
        categories={placeCategories}
        loading={loading}
      />
    );
  };
  if (!token || !user) {
    return (
      <AuthPage
        needsBootstrap={needsBootstrap}
        bootstrapUser={bootstrapUser}
        canDevReset={canDevReset}
        authForm={authForm}
        setAuthForm={setAuthForm}
        onSubmit={submitAuth}
        loading={loading}
        message={message}
      />
    );
  }
  return (
    <AppLayout activeTab={activeTab} onTabChange={setActiveTab} user={user} onLogout={logout}>
      {message && (
        <Stack
          role="status"
          aria-live="polite"
          sx={String(`toast-message ${messageType}`)
            .split(/\s+/)
            .map(
              (name) =>
                ({
                  'toast-message': {
                    position: 'fixed',
                    top: '20px',
                    left: '20px',
                    zIndex: '80',
                    width: 'min(420px, calc(100vw - 40px))',
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '12px 14px',
                    border: '1px solid var(--line)',
                    borderRadius: '12px',
                    background: 'white',
                    boxShadow: '0 18px 48px rgba(15, 23, 42, 0.18)',
                    fontWeight: '800',
                    animation: 'toast-in 160ms ease-out',
                    '& button': {
                      width: '30px',
                      height: '30px',
                      minHeight: '30px',
                      flex: '0 0 auto',
                      padding: '0',
                      borderRadius: '999px',
                      background: 'rgba(15, 23, 42, 0.08)',
                      color: 'inherit',
                      fontSize: '20px',
                      lineHeight: '1',
                    },
                  },
                })[name],
            )
            .filter(Boolean)}
        >
          <Typography>{message}</Typography>
          <Button type="button" aria-label="סגור הודעה" onClick={() => setMessage('')}>
            ×
          </Button>
        </Stack>
      )}
      {renderPage()}
    </AppLayout>
  );
};
export default App;
